import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import TabButton from "../components/tab-button";
import TabBar from "../components/tab-bar";
import PageShell from "../components/page-shell";
import FilterField from "../components/mobile/filter-field";
import { DesktopOnly } from "../components/responsive";
import SelectInput from "../components/select-input";
import DataTable, { TableRowData } from "../components/data-table";
import { customersTableColumns } from "../lib/table-columns";
import FormDrawer from "../components/form-drawer";
import {
  individualCustomerFormFields,
  organizationCustomerFormFields,
} from "../lib/form-fields";
import SearchInput from "../components/search-input";
import { Edit } from "lucide-react";
import {
  getCustomers,
  getCustomerStatistics,
  createCustomer,
  updateCustomer,
  CreateCustomerPayload,
  CustomerStatistics,
} from "../lib/api";
import BarGraph from "../components/bar-graph";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import { toast } from "sonner";
import { getBackendErrorMessage } from "../lib/utils";

export type CustomersFiltersState = {
  searchTerm: string;
  searchInput: string;
  currentTab: string;
  selectedRegion: string;
};

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [formValues, setFormValues] = useState<{
    [key: string]: unknown;
  } | null>(null);

  const [formFields, setFormFields] = useState(
    individualCustomerFormFields.slice(0, 1), // Start with just Type selection
  );

  const queryClient = useQueryClient();

  const typeParam =
    currentTab === "Individual"
      ? ("INDIVIDUAL" as const)
      : currentTab === "Organization"
        ? ("ORGANIZATION" as const)
        : undefined;

  const customersQuery = useQuery({
    queryKey: ["customers", { page: currentPage, limit: pageSize, search: searchTerm, type: typeParam }],
    queryFn: async () => getCustomers(currentPage, pageSize, searchTerm, typeParam),
    placeholderData: keepPreviousData,
  });

  const statisticsQuery = useQuery<CustomerStatistics>({
    queryKey: ["customers", "statistics"],
    queryFn: async () => getCustomerStatistics(),
  });

  const mappedCustomers = useMemo(() => {
    const data = customersQuery.data?.data ?? [];
    return data.map((c) => {
      const addressParts = [c.street, c.ward, c.district, c.region].filter(Boolean);
      const address = addressParts.length > 0 ? addressParts.join(", ") : c.region || "-";
      const pointOfContact =
        c.pointOfContactName && c.pointOfContactPhone
          ? `${c.pointOfContactName}, ${c.pointOfContactPhone}`
          : c.pointOfContactName || c.pointOfContactPhone || "-";
      return {
        id: c.id,
        name: c.fullName,
        type: c.type === "ORGANIZATION" ? "Organization" : "Individual",
        address,
        country: c.country || "-",
        phone_number: c.phoneNumber,
        point_of_contact: pointOfContact,
        tin: c.tin || "-",
        vrn: c.vrn || "-",
        raw_data: c,
      };
    }) as TableRowData[];
  }, [customersQuery.data]);

  const customers = useMemo(() => {
    if (!selectedRegion || selectedRegion === "all") return mappedCustomers;
    return mappedCustomers.filter((c) => (c as any).raw_data?.region === selectedRegion);
  }, [mappedCustomers, selectedRegion]);

  const initialLoading = customersQuery.isLoading || statisticsQuery.isLoading;

  // no-op: queries react to deps

  // Restore filters when returning from CustomerDetails (state passed back via navigate)
  const hasRestoredFiltersRef = React.useRef(false);
  useEffect(() => {
    const customersFilters = (location.state as { customersFilters?: CustomersFiltersState })?.customersFilters;
    if (customersFilters && !hasRestoredFiltersRef.current) {
      hasRestoredFiltersRef.current = true;
      setSearchTerm(customersFilters.searchTerm ?? "");
      setSearchInput(customersFilters.searchInput ?? "");
      setCurrentTab(customersFilters.currentTab ?? "All");
      setSelectedRegion(customersFilters.selectedRegion ?? "all");
    }
  }, [location.state]);

  // Reset pagination on tab or region changes (search handled separately).
  useEffect(() => {
    setCurrentPage(1);
  }, [currentTab, selectedRegion]);

  // Debounce raw search input and apply to `searchTerm` after a short delay
  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // When searchTerm changes (debounced above), perform a search without showing
  // the page loading state. If we're not already on page 1, set the ref and
  // reset page to 1 so the page effect will run suppressed; otherwise fetch
  // directly suppressed.
  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1);
  }, [searchTerm]);

  const actions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        const raw = (row as any).raw_data;
        navigate("/customer-details", {
          state: {
            id: raw.id,
            fromCustomersFilters: {
              searchTerm,
              searchInput,
              currentTab,
              selectedRegion,
            },
          },
        });
      },
    },
    {
      label: "Edit customer",
      icon: Edit,
      onClick: (row: TableRowData) => {
        const raw = (row as any).raw_data;
        setEditingId(raw.id);

        setFormValues({
          type: raw.type === "ORGANIZATION" ? "Organization" : "Individual",
          name: raw.fullName,
          street: raw.street,
          address: `${raw.ward ? raw.ward + ", " : ""}${raw.district}, ${raw.region}`,
          phone_number: raw.phoneNumber,
          contact_name: raw.pointOfContactName,
          contact_phone_number: raw.pointOfContactPhone,
          postal_code: raw.postalCode,
          tin: raw.tin,
          vrn: raw.vrn,
        });
      },
    },
  ];

  const handleFormSubmit = async () => {
    if (!formValues) return;

    setIsSubmitting(true);
    try {
      // Parse address "Ward, District, Region"
      const addressStr = (formValues.address as string) || "";
      const addressParts = addressStr.split(",").map((s) => s.trim());
      const region =
        addressParts.length > 0 ? addressParts[addressParts.length - 1] : "";
      const district =
        addressParts.length > 1 ? addressParts[addressParts.length - 2] : "";
      const ward =
        addressParts.length > 2 ? addressParts[addressParts.length - 3] : "";

      // Construct payload
      const payload: CreateCustomerPayload = {
        fullName: formValues.name as string,
        phoneNumber: formValues.phone_number as string,
        type: (formValues.type as string).toUpperCase() as
          | "ORGANIZATION"
          | "INDIVIDUAL",
        pointOfContactName:
          (formValues.contact_name as string) || (formValues.name as string),
        pointOfContactPhone:
          (formValues.contact_phone_number as string) ||
          (formValues.phone_number as string),
        street: (formValues.street as string) || "",
        ward: ward || "",
        district: district || "",
        region: region || "",
        country: "Tanzania",
        postalCode: (formValues.postal_code as string) || "",
        tin: (formValues.tin as string) || "",
        vrn: (formValues.vrn as string) || "",
      };

      if (editingId) {
        await updateCustomer(editingId, payload);
      } else {
        await createCustomer(payload);
      }
      setFormValues(null);
      setEditingId(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customers"] }),
        queryClient.invalidateQueries({ queryKey: ["customers", "statistics"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save customer"));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (formValues?.type === "Individual") {
      setFormFields(individualCustomerFormFields);
    }
    if (formValues?.type === "Organization") {
      setFormFields(organizationCustomerFormFields);
    }
  }, [formValues?.type]);

  // Calculate percentages for BarGraph
  const totalRegionsCount =
    statisticsQuery.data?.byRegion.reduce((acc, curr) => acc + curr.count, 0) || 1;
  const regionMetrics =
    (statisticsQuery.data?.byRegion.map((r) => ({
      label: r.region,
      count: r.count,
      percentage: Math.round((r.count / totalRegionsCount) * 100),
      avgDays: 0,
    })) || []).sort((a, b) => b.count - a.count);

  // Data for HorizontalBarGraph (Using totals)
  const transactionalData = [
    {
      category: "Overview",
      gap: 0.2,
      values: [
        {
          label: "Sites",
          value: statisticsQuery.data?.totals.sites || 0,
          fillColor: "#bce3ff",
        },
        {
          label: "Quotations",
          value: statisticsQuery.data?.totals.quotations || 0,
          fillColor: "#e9d5ff",
        },
        {
          label: "Invoices",
          value: statisticsQuery.data?.totals.invoices || 0,
          fillColor: "#ffd6a8",
        },
      ],
    },
  ];

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["customers"] }),
      queryClient.invalidateQueries({ queryKey: ["customers", "statistics"] }),
    ]);

  const openNewCustomer = () => {
    setFormValues({});
    setEditingId(null);
  };

  const regionSelect = (
    <SelectInput
      value={selectedRegion}
      options={[
        { label: "All Regions", value: "all" },
        ...(statisticsQuery.data?.byRegion
          ?.filter((r) => r.region != null && String(r.region).trim() !== "")
          .map((r) => ({ label: r.region, value: r.region })) || []),
      ]}
      placeholder="Region"
      onChange={setSelectedRegion}
    />
  );

  return (
    <>
      <FormDrawer
        title={editingId ? "Edit Customer" : "Add New Customer"}
        buttonText={editingId ? "Update Customer" : "Add Customer"}
        formValues={formValues}
        setFormValues={setFormValues}
        fields={formFields}
        handleSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        onClose={() => {
          setFormFields(individualCustomerFormFields.slice(0, 1));
          setFormValues(null);
          setEditingId(null);
        }}
      />
      <PageShell
        title="Customers"
        description="Keep an organized record of all your customers and their rental activities."
        loading={initialLoading}
        toolbar={
          <>
            <RefreshToolbarButton
              isRefreshing={customersQuery.isFetching || statisticsQuery.isFetching}
              onRefresh={refresh}
            />
            <Button variant="default" className="h-9" onClick={openNewCustomer}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              New Customer
            </Button>
          </>
        }
        stats={[
          {
            label: "Total Customers",
            value: statisticsQuery.data?.totalCustomers.toString() || "0",
            icon: <Icons.Clients className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Organization",
            value: statisticsQuery.data?.byType.organization.toString() || "0",
            icon: <Icons.Building className="w-5 h-5 text-blue-600" />,
          },
          {
            label: "Individual",
            value: statisticsQuery.data?.byType.individual.toString() || "0",
            icon: <Icons.Clients className="w-5 h-5 text-blue-600" />,
          },
        ]}
        insights={
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,400px] gap-4 mt-6">
            <HorizontalBarGraph
              graphTitle="Transactional Summary"
              data={transactionalData.slice(0, 3)}
            />
            <BarGraph graphTitle="Customers by Region" metrics={regionMetrics.slice(0, 3)} />
          </div>
        }
        mobile={{
          onRefresh: refresh,
          primaryAction: { label: "New Customer", icon: Icons.Plus, onClick: openNewCustomer },
          filters: (
            <FilterField label="Region">{regionSelect}</FilterField>
          ),
          activeFilterCount: selectedRegion !== "all" ? 1 : 0,
          onResetFilters: () => setSelectedRegion("all"),
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabBar>
            {[
              { label: "All", count: statisticsQuery.data?.totalCustomers || 0 },
              { label: "Organization", count: statisticsQuery.data?.byType.organization || 0 },
              { label: "Individual", count: statisticsQuery.data?.byType.individual || 0 },
            ].map((tab) => (
              <TabButton
                key={tab.label}
                label={tab.label}
                count={tab.count}
                active={currentTab === tab.label}
                onClick={() => setCurrentTab(tab.label)}
              />
            ))}
          </TabBar>
          <DesktopOnly>{regionSelect}</DesktopOnly>
        </div>
        <SearchInput
          searchTerm={searchInput}
          onSearchChange={(value) => setSearchInput(value)}
          placeholder="Search customers..."
        />
        <DataTable
          columns={customersTableColumns}
          data={customers}
          actions={actions}
          loading={customersQuery.isFetching}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems: customersQuery.data?.meta.total ?? 0,
            totalPages: customersQuery.data?.meta.totalPages ?? 1,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
          }}
        />
      </PageShell>
    </>
  );
};

export default Customers;
